import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Search, ChevronRight, User, LogOut, Settings } from 'lucide-react';
import { getCategoryBySlug } from '../../config/categories.config';
import { useAuth } from '../../auth/useAuth';
import { useUIStore } from '../../store';
import { useFilterStore } from '../../hooks/useFilters';

/**
 * Get the breadcrumb display name from the current route path.
 */
function getBreadcrumb(pathname: string): { section: string; label: string } {
  const slug = pathname.replace(/^\//, '').split('/')[0];

  if (slug === 'dashboard' || slug === '') {
    return { section: 'Dashboard', label: 'Overview' };
  }
  if (slug === 'archive') {
    return { section: 'Archive', label: 'Historical Search' };
  }
  if (slug === 'weekly-reports') {
    return { section: 'Reports', label: 'Weekly Reports' };
  }
  if (slug === 'settings') {
    return { section: 'Administration', label: 'Settings' };
  }

  const category = getCategoryBySlug(slug);
  if (category) {
    return { section: 'News', label: category.displayName };
  }

  return { section: 'Portal', label: slug };
}

/**
 * Top header bar with breadcrumb, search, and user controls.
 */
export const Header: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { notifications } = useUIStore();
  const { searchQuery, setSearch } = useFilterStore();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const breadcrumb = getBreadcrumb(location.pathname);
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-10 h-16 bg-white border-b border-[#EDE6DE] shadow-sm flex items-center px-6 gap-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="text-[#6B7280] font-medium">{breadcrumb.section}</span>
        <ChevronRight size={14} className="text-[#6B7280]/50 flex-shrink-0" />
        <span className="text-[#1A2332] font-semibold truncate">{breadcrumb.label}</span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search bar */}
      <div className="hidden md:flex items-center max-w-md w-full">
        <div className="relative w-full">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]"
          />
          <input
            type="text"
            placeholder="Search articles, companies, commodities..."
            value={searchQuery}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-[#F5F0EB] border border-transparent rounded-full
                       placeholder-[#6B7280]/60 text-[#1A2332]
                       focus:outline-none focus:border-[#1A2332]/20 focus:bg-white focus:shadow-sm
                       transition-all duration-200"
          />
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right actions */}
      <div className="flex items-center gap-3">
        {/* Notification bell */}
        <button
          className="relative p-2 rounded-lg hover:bg-[#F5F0EB] text-[#6B7280] hover:text-[#1A2332] transition-colors"
          aria-label="Notifications"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-[#C08B5C] rounded-full">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* User avatar dropdown */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[#F5F0EB] transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-[#1A2332] flex items-center justify-center text-xs font-semibold text-[#D4A574]">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <span className="hidden lg:block text-sm font-medium text-[#3A3F47] max-w-[120px] truncate">
              {user?.name || 'User'}
            </span>
          </button>

          {/* Dropdown menu */}
          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-[#EDE6DE] py-2 animate-slide-up">
              <div className="px-4 py-2 border-b border-[#EDE6DE]">
                <p className="text-sm font-semibold text-[#1A2332]">{user?.name}</p>
                <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
              </div>
              <div className="py-1">
                <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-[#3A3F47] hover:bg-[#F5F0EB] transition-colors">
                  <User size={16} />
                  <span>Profile</span>
                </button>
                <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-[#3A3F47] hover:bg-[#F5F0EB] transition-colors">
                  <Settings size={16} />
                  <span>Preferences</span>
                </button>
              </div>
              <div className="border-t border-[#EDE6DE] pt-1">
                <button
                  onClick={logout}
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
